import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Activity,
  Brain,
  Pause,
  Play,
  Check,
  Trash2,
  History,
  Clock,
  AlertTriangle,
  Settings as SettingsIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { celebrate, edgeFlash } from "@/lib/effects";
import TimerPanel from "@/components/TimerPanel";
import InterruptButton from "@/components/InterruptButton";
import BreadcrumbFeed from "@/components/BreadcrumbFeed";
import StartSessionForm from "@/components/StartSessionForm";
import SessionHistory from "@/components/SessionHistory";
import ResumeModal from "@/components/ResumeModal";
import InterruptDialog from "@/components/InterruptDialog";
import MomentumMeter from "@/components/MomentumMeter";
import SettingsDialog from "@/components/SettingsDialog";
import { Button } from "@/components/ui/button";

const fmtClock = (s) => {
  const sign = s < 0 ? "-" : "";
  const abs = Math.abs(s);
  const m = Math.floor(abs / 60);
  const sec = abs % 60;
  return `${sign}${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
};

export default function Dashboard() {
  const { play } = useSettings();
  const [active, setActive] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [crumbs, setCrumbs] = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [interruptOpen, setInterruptOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [resumeLoading, setResumeLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [momentum, setMomentum] = useState(0);
  const [momentumPeak, setMomentumPeak] = useState(0);
  const momentumRef = useRef({ score: 0, peak: 0 });
  const alarmedRef = useRef(false);

  const loadAll = useCallback(async () => {
    const [act, list] = await Promise.all([
      api.getActive(),
      api.listSessions(),
    ]);
    setActive(act);
    setSessions(list);
    if (act) {
      const c = await api.listCrumbs(act.id);
      setCrumbs(c);
      setElapsed(act.elapsed_seconds || 0);
      alarmedRef.current = false;
    } else {
      setCrumbs([]);
      setElapsed(0);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Tick timer locally when active
  useEffect(() => {
    if (!active || active.status !== "active") return;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [active]);

  // Persist elapsed every 15s
  useEffect(() => {
    if (!active || active.status !== "active") return;
    const id = setInterval(() => {
      api
        .updateSession(active.id, { elapsed_seconds: elapsed })
        .catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [active, elapsed]);

  // Momentum ticker: +1 every 3s of unbroken focus. Resets on interrupt / new session.
  useEffect(() => {
    if (!active || active.status !== "active") return;
    const id = setInterval(() => {
      setMomentum((s) => {
        const next = s + 1;
        setMomentumPeak((p) => (next > p ? next : p));
        momentumRef.current = { score: next, peak: Math.max(momentumRef.current.peak, next) };
        return next;
      });
    }, 3000);
    return () => clearInterval(id);
  }, [active]);

  // Alarm when timer finishes
  useEffect(() => {
    if (!active || active.status !== "active") return;
    const remaining = (active.planned_duration_seconds || 0) - elapsed;
    if (remaining <= 0 && !alarmedRef.current) {
      alarmedRef.current = true;
      play("complete");
      celebrate("timer");
      edgeFlash("green");
      toast(`Timer finished: ${active.title}`, {
        description: "Time's up — log progress, take a break, or extend.",
      });
    }
  }, [elapsed, active, play]);

  const handleStart = async (payload) => {
    const s = await api.createSession(payload);
    setMomentum(0);
    setMomentumPeak(0);
    momentumRef.current = { score: 0, peak: 0 };
    play("start");
    edgeFlash("green");
    toast.success(`Focus session started: ${s.title}`);
    await loadAll();
  };

  const handlePauseResume = async () => {
    if (!active) return;
    const next = active.status === "active" ? "paused" : "active";
    await api.updateSession(active.id, { status: next, elapsed_seconds: elapsed });
    toast(next === "active" ? "Resumed" : "Paused");
    await loadAll();
  };

  const handleComplete = async () => {
    if (!active) return;
    const finalPeak = Math.max(momentumRef.current.peak, momentumPeak);
    const sessionId = active.id;
    await api.updateSession(sessionId, {
      status: "completed",
      elapsed_seconds: elapsed,
    });
    play("complete");
    celebrate("complete");
    edgeFlash("green");
    toast.success("Session completed", {
      description: `Peak momentum: ${finalPeak}. Fetching praise…`,
    });
    // Fire-and-catch — praise is a nice-to-have
    try {
      const res = await api.praise(sessionId, finalPeak);
      if (res?.praise) {
        toast(res.praise, { duration: 8000 });
      }
    } catch {
      // ignore — session still saved
    }
    setMomentum(0);
    setMomentumPeak(0);
    momentumRef.current = { score: 0, peak: 0 };
    await loadAll();
  };

  const handleDelete = async (id) => {
    await api.deleteSession(id);
    toast("Session deleted");
    await loadAll();
  };

  const handleAddCrumb = async (content, type = "note") => {
    if (!active) return;
    const c = await api.addCrumb(active.id, { content, type });
    setCrumbs((prev) => [...prev, c]);
    play("crumb");
  };

  const handleVoice = async (blob) => {
    if (!active) return;
    try {
      toast("Transcribing voice note…");
      const res = await api.transcribe(active.id, blob);
      if (res.breadcrumb) {
        setCrumbs((prev) => [...prev, res.breadcrumb]);
        play("voice");
      }
      toast.success("Voice breadcrumb added");
    } catch (e) {
      toast.error("Transcription failed. Try again?");
    }
  };

  const submitInterrupt = async ({ next_step, note }) => {
    if (!active) return;
    const crumb = await api.interrupt(active.id, { next_step, note });
    setCrumbs((prev) => [...prev, crumb]);
    setInterruptOpen(false);
    play("interrupt");
    edgeFlash("pink");
    // Reset momentum on interrupt
    setMomentum(0);
    momentumRef.current.score = 0;
    toast("Train of thought saved. Go handle it.", {
      description: `Next step locked: ${next_step}`,
    });
    await loadAll();
  };

  const resumeSession = async (sessionId) => {
    setResumeLoading(true);
    setResumeOpen(true);
    setResumeText("");
    try {
      // Set active first so user sees it switch
      await api.updateSession(sessionId, { status: "active" });
      const updated = await api.resumeSummary(sessionId);
      setResumeText(updated.resume_summary || "");
      await loadAll();
    } catch (e) {
      toast.error("Couldn't generate resume brief");
      setResumeOpen(false);
    } finally {
      setResumeLoading(false);
    }
  };

  const remaining = active
    ? (active.planned_duration_seconds || 0) - elapsed
    : 0;

  const isIdle = !active || active.status !== "active";

  return (
    <div className="min-h-screen text-white" data-testid="dashboard-root">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 md:px-10 py-6 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[hsl(var(--green))]/15 border border-[hsl(var(--green))]/40 flex items-center justify-center">
            <Brain className="w-5 h-5 text-[hsl(var(--green))]" />
          </div>
          <div>
            <div className="font-display text-xl md:text-2xl font-black tracking-tight">
              Trainwreck<span className="text-[hsl(var(--green))]">.</span>
            </div>
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40">
              The interrupt-friendly focus tracker
            </div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-4 text-xs font-mono text-white/50">
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-[hsl(var(--green))]" />
            <span>
              {sessions.length} session{sessions.length === 1 ? "" : "s"} on
              record
            </span>
          </div>
          <button
            onClick={() => setSettingsOpen(true)}
            className="w-9 h-9 rounded-xl border border-white/10 hover:border-[hsl(var(--green))]/40 hover:text-[hsl(var(--green))] flex items-center justify-center transition"
            data-testid="open-settings-button"
            title="Sound & effects"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-8 md:py-12">
        {!active ? (
          <section className="max-w-3xl">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[hsl(var(--green))] mb-3">
              No active session
            </p>
            <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6">
              Start a thought.
              <br />
              We&apos;ll{" "}
              <span className="text-[hsl(var(--pink))]">remember</span> where
              you left it.
            </h1>
            <p className="text-white/60 text-base md:text-lg max-w-2xl mb-10 leading-relaxed">
              Drop one task in the box. When life interrupts —
              <span className="text-white"> hit the pink button</span>. We log
              your next step, your half-thoughts, and your voice notes. When
              you&apos;re back, we hand you a calm 60-second brief on exactly
              where to resume.
            </p>
            <StartSessionForm onStart={handleStart} />
            <div className="mt-12">
              <SessionHistory
                sessions={sessions}
                onResume={resumeSession}
                onDelete={handleDelete}
              />
            </div>
          </section>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 items-start">
            {/* LEFT — Timer + session card */}
            <div className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-6">
              <TimerPanel
                session={active}
                elapsed={elapsed}
                remaining={remaining}
                fmt={fmtClock}
              />

              <MomentumMeter score={momentum} peak={momentumPeak} />

              <div className="card-elevated p-6" data-testid="session-controls-card">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <div className="min-w-0">
                    <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1">
                      Current task
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl font-black truncate" data-testid="active-session-title">
                      {active.title}
                    </h2>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-full text-[10px] font-mono uppercase tracking-widest border ${
                      active.status === "active"
                        ? "border-[hsl(var(--green))]/50 text-[hsl(var(--green))] bg-[hsl(var(--green))]/10"
                        : "border-white/20 text-white/60 bg-white/5"
                    }`}
                    data-testid="session-status-badge"
                  >
                    {active.status}
                  </span>
                </div>

                {active.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {active.tags.map((t) => (
                      <span key={t} className="tag-pill on" data-testid="session-tag">
                        #{t}
                      </span>
                    ))}
                  </div>
                )}

                {active.next_step && (
                  <div className="mb-5 p-4 rounded-xl border border-[hsl(var(--pink))]/30 bg-[hsl(var(--pink))]/5">
                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[hsl(var(--pink))] mb-1">
                      Locked next step
                    </div>
                    <div className="text-sm text-white/85" data-testid="locked-next-step">
                      {active.next_step}
                    </div>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    onClick={handlePauseResume}
                    className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl h-11"
                    data-testid="toggle-pause-resume-button"
                  >
                    {active.status === "active" ? (
                      <>
                        <Pause className="w-4 h-4 mr-2" /> Pause
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" /> Resume
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleComplete}
                    className="flex-1 bg-[hsl(var(--green))]/15 hover:bg-[hsl(var(--green))]/25 border border-[hsl(var(--green))]/40 text-[hsl(var(--green))] rounded-xl h-11"
                    data-testid="complete-session-button"
                  >
                    <Check className="w-4 h-4 mr-2" /> Complete
                  </Button>
                  <Button
                    onClick={() => handleDelete(active.id)}
                    className="bg-transparent hover:bg-white/5 border border-white/10 text-white/50 hover:text-white rounded-xl h-11 px-3"
                    data-testid="delete-session-button"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                <button
                  onClick={() => resumeSession(active.id)}
                  className="mt-3 w-full text-xs font-mono uppercase tracking-widest text-white/40 hover:text-[hsl(var(--green))] transition py-2"
                  data-testid="ai-resume-trigger"
                >
                  <Brain className="w-3.5 h-3.5 inline mr-2" />
                  Ask AI: where was I?
                </button>
              </div>
            </div>

            {/* MIDDLE — Breadcrumb feed */}
            <div className="lg:col-span-4">
              <BreadcrumbFeed
                crumbs={crumbs}
                onAdd={handleAddCrumb}
                onVoice={handleVoice}
              />
            </div>

            {/* RIGHT — Interrupt + history */}
            <div className="lg:col-span-3 flex flex-col gap-6 lg:sticky lg:top-6">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-3 flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-[hsl(var(--pink))]" />
                  Emergency
                </div>
                <InterruptButton
                  onClick={() => setInterruptOpen(true)}
                  idle={isIdle}
                />
                <p className="mt-3 text-xs text-white/40 leading-relaxed">
                  Smash this when something pulls you away. We pause the
                  timer, lock your next step, and keep the breadcrumbs warm.
                </p>
              </div>

              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-3 flex items-center gap-2">
                  <History className="w-3.5 h-3.5 text-[hsl(var(--green))]" />
                  Past trains
                </div>
                <SessionHistory
                  sessions={sessions.filter((s) => s.id !== active.id)}
                  onResume={resumeSession}
                  onDelete={handleDelete}
                  compact
                />
              </div>
            </div>
          </div>
        )}
      </main>

      <InterruptDialog
        open={interruptOpen}
        onOpenChange={setInterruptOpen}
        onSubmit={submitInterrupt}
        currentTask={active?.title}
      />

      <ResumeModal
        open={resumeOpen}
        onOpenChange={setResumeOpen}
        text={resumeText}
        loading={resumeLoading}
        task={active?.title}
      />

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <footer className="px-6 md:px-10 py-8 text-xs font-mono text-white/30 flex items-center justify-between border-t border-white/5">
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3" /> Built for brains that bounce.
        </div>
        <div>v0.1 · single-user mode</div>
      </footer>
    </div>
  );
}
